import { useState } from "react";
import api from "@/lib/api";
import { TIPI_CAUSALE, Field, FieldDate, BigField, segnoForTipo } from "./utils";
import { FornitoreField, PagamentoField } from "./FornitoreField";
import { RigheTable } from "./RigheTable";
import { AddRigaModal } from "./AddRigaModal";
import { RipartoWizard } from "./RipartoWizard";

export function FatturaEditor({ f, onClose, onDeleted }) {
  const [doc, setDoc] = useState(f);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const [showRiparto, setShowRiparto] = useState(false);
  const [showAddRiga, setShowAddRiga] = useState(false);

  const isNew = !!doc._isNew;
  const id_doc = doc.id_doc;
  const set = (k, v) => setDoc(d => ({ ...d, [k]: v }));
  const isExtra = Number(doc.prot_tipo_id) === 3;
  const segnoCorrente = segnoForTipo(doc.prot_tipo_id, doc.segno);

  const salva = async () => {
    setSaving(true); setErr(null);
    try {
      const body = {
        prot_anno: Number(doc.prot_anno),
        prot_tipo_id: Number(doc.prot_tipo_id || 1),
        data_fattura: doc.data_fattura,
        data_registrazione: doc.data_registrazione || null,
        numero_fattura: doc.numero_fattura || "",
        id_cliente: doc.id_cliente || null,
        id_pagamento: doc.id_pagamento != null ? Number(doc.id_pagamento) : null,
        totale_iva_compresa: Number(doc.totale_iva_compresa || 0),
        segno: segnoCorrente,
        descrizione_extra: doc.descrizione_extra || null,
      };
      if (isNew) { const r = await api.post("/pn/fatture", body); setDoc({ ...r.data }); }
      else { const r = await api.put(`/pn/fatture/${id_doc}`, body); setDoc(r.data); }
    } catch (e) { setErr(e.response?.data?.detail || "Errore salvataggio"); }
    finally { setSaving(false); }
  };

  const ricarica = async () => {
    if (isNew) return;
    const r = await api.get(`/pn/fatture/${id_doc}`); setDoc(r.data);
  };

  const eliminaFattura = async () => {
    if (isNew) { onClose(); return; }
    if (!window.confirm("Eliminare la fattura e tutte le sue righe contabili?")) return;
    try {
      await api.delete(`/pn/fatture/${id_doc}`);
      if (onDeleted) onDeleted(id_doc);
      onClose();
    } catch (e) {
      alert("Errore eliminazione: " + (e.response?.data?.detail || e.message));
    }
  };

  const eliminaRiga = async () => {
    if (!isNew && window.confirm("Cancellare TUTTE le righe contabili?")) {
      await api.delete(`/pn/fatture/${id_doc}/righe/all`);
      await ricarica();
    }
  };

  const tipoLabel = ({1:"Fatture Acquisto",2:"Nota di Credito",3:"Operazione Extra"})[doc.prot_tipo_id] || "—";

  const todayDmy = () => {
    const d = new Date();
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    return `${dd}/${mm}/${d.getFullYear()}`;
  };

  const nuovaFattura = () => {
    const thisYear = new Date().getFullYear();
    if (!isNew && (doc.id_doc) && !window.confirm("Aprire una nuova fattura? Le modifiche non salvate andranno perse.")) return;
    setDoc({ _isNew: true, prot_anno: doc.prot_anno || thisYear, prot_tipo_id: 1, data_fattura: "", data_registrazione: todayDmy(), numero_fattura: "", id_cliente: null, id_pagamento: null, totale_iva_compresa: 0, segno: "+" });
    setErr(null);
  };

  const cambiaTipo = (tid) => {
    const newSegno = segnoForTipo(tid, doc.segno);
    setDoc(d => ({ ...d, prot_tipo_id: tid, segno: newSegno }));
  };

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 600, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div onClick={e => e.stopPropagation()} data-testid="pn-fat-editor"
        style={{ background: "white", width: "98vw", height: "97vh", display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <div style={{ background: "#90caf9", padding: "8px 12px", display: "flex", gap: 8, alignItems: "stretch", flexShrink: 0 }}>
          <select value={doc.prot_anno || ""} onChange={e => set("prot_anno", Number(e.target.value))} disabled={!isNew}
            style={{ height: 44, boxSizing: "border-box", padding: "0 10px", fontSize: 14, fontWeight: 700, border: "2px solid #37474f" }}>
            {[2024, 2025, 2026, 2027].map(a => <option key={a} value={a}>{a}</option>)}
          </select>
          <select value={doc.prot_tipo_id || 1} onChange={e => cambiaTipo(Number(e.target.value))} disabled={!isNew}
            style={{ height: 44, boxSizing: "border-box", padding: "0 22px", fontSize: 15, fontWeight: 800, letterSpacing: 0.5, background: "#1565c0", color: "white", border: "2px solid #0d47a1", borderRadius: 0, cursor: isNew ? "pointer" : "default", textTransform: "uppercase" }}>
            {TIPI_CAUSALE.map(t => <option key={t.id} value={t.id}>{t.id} {t.l}</option>)}
          </select>
          {/* Segno: editabile solo per Operazione Extra (tipo 3).
              Mapping: Extra segno '+' → RICAVO; Extra segno '-' → COSTO (sempre additivo, mai negativo) */}
          {isExtra ? (
            <div style={{ display: "flex", height: 44, boxSizing: "border-box", border: "2px solid #555" }}>
              <button onClick={() => set("segno", "+")} type="button" data-testid="pn-fat-segno-ricavo"
                title="Operazione Extra registrata come RICAVO"
                style={{ padding: "0 14px", background: doc.segno !== "-" ? "#1b5e20" : "white", color: doc.segno !== "-" ? "white" : "#1b5e20", border: "none", fontWeight: 800, fontSize: 14, cursor: "pointer" }}>+ RICAVO</button>
              <button onClick={() => set("segno", "-")} type="button" data-testid="pn-fat-segno-costo"
                title="Operazione Extra registrata come COSTO"
                style={{ padding: "0 14px", background: doc.segno === "-" ? "#ef6c00" : "white", color: doc.segno === "-" ? "white" : "#ef6c00", border: "none", borderLeft: "1px solid #555", fontWeight: 800, fontSize: 14, cursor: "pointer" }}>+ COSTO</button>
            </div>
          ) : (
            <span data-testid="pn-fat-segno-locked"
              style={{ height: 44, boxSizing: "border-box", padding: "0 14px", display: "inline-flex", alignItems: "center", background: segnoCorrente === "-" ? "#c62828" : "#1b5e20", color: "white", fontWeight: 800, fontSize: 14 }}
              title="Tipo automatico: Fatture=+ COSTO, Note Credito=- COSTO">
              {segnoCorrente === "-" ? "− COSTO (auto)" : "+ COSTO (auto)"}
            </span>
          )}
          {/* CREA REGISTRAZIONE: a sinistra, visibile solo quando isNew */}
          {isNew && (
            <button onClick={salva} disabled={saving} data-testid="pn-fat-crea-header"
              title="Crea la registrazione e assegna il protocollo: poi potrai compilare numero, data, fornitore..."
              style={{
                height: 44,
                boxSizing: "border-box",
                padding: "0 22px",
                background: "linear-gradient(180deg, #2e7d32 0%, #1b5e20 100%)",
                color: "white",
                border: "2px solid #0d3a10",
                fontSize: 15,
                fontWeight: 800,
                letterSpacing: 0.5,
                cursor: saving ? "wait" : "pointer",
                boxShadow: "0 3px 10px rgba(27,94,32,0.45)",
                textTransform: "uppercase",
              }}>
              {saving ? "Creazione..." : "💾 Crea Registrazione"}
            </button>
          )}
          {/* Cella protocollo: arancione "AL SALVATAGGIO" finché isNew, poi mostra il protocollo assegnato (stesso stile) */}
          <span data-testid="pn-fat-prot-cell"
            title={isNew ? "Il numero di protocollo viene assegnato cliccando 'CREA REGISTRAZIONE'" : "Numero di protocollo assegnato"}
            style={{ height: 44, boxSizing: "border-box", padding: "0 14px", display: "inline-flex", alignItems: "center", gap: 6, background: "#ffc107", color: "#5d4037", fontWeight: 800, fontSize: 14, fontFamily: "monospace", border: isNew ? "2px dashed #8a6d3b" : "2px solid #8a6d3b", letterSpacing: 0.3 }}>
            {isNew ? <><span>⏳</span> PROT. AL SALVATAGGIO</> : <>PROT. {doc.prot_anno}/{doc.prot_numero}</>}
          </span>
          <span style={{ fontSize: 12, color: "#37474f", flex: 1, fontStyle: "italic", display: "flex", alignItems: "center" }}>{tipoLabel}</span>
          <button onClick={ricarica} disabled={isNew} title="Ricarica"
            style={{ height: 44, boxSizing: "border-box", padding: "0 14px", background: "white", border: "2px solid #1565c0", color: "#1565c0", fontWeight: 700, fontSize: 16, cursor: "pointer" }}>↻</button>
          <button onClick={nuovaFattura} data-testid="pn-fat-new-from-detail" title="Crea una nuova operazione"
            style={{
              height: 44,
              boxSizing: "border-box",
              padding: "0 22px",
              background: "linear-gradient(180deg, #d32f2f 0%, #b71c1c 100%)",
              color: "white",
              border: "2px solid #7f0000",
              fontSize: 15,
              fontWeight: 800,
              letterSpacing: 0.5,
              cursor: "pointer",
              boxShadow: "0 3px 10px rgba(183,28,28,0.45)",
              textTransform: "uppercase",
            }}>
            <span style={{ color: "white", fontWeight: 900, marginRight: 4 }}>+</span> Nuova Operazione
          </button>
          <button onClick={eliminaFattura} title="Elimina fattura"
            style={{ height: 44, boxSizing: "border-box", padding: "0 14px", background: "white", border: "2px solid #c62828", color: "#c62828", fontWeight: 700, fontSize: 16, cursor: "pointer" }}>🗑</button>
          <button onClick={onClose}
            style={{ height: 44, boxSizing: "border-box", padding: "0 16px", background: "white", border: "2px solid #555", color: "#333", fontWeight: 700, fontSize: 14, cursor: "pointer" }}>✕ Chiudi</button>
        </div>
        {err && <div style={{ padding: 8, background: "#fff0f0", color: "#c00", fontSize: 12, flexShrink: 0 }}>{err}</div>}
        {isNew ? (
          <div data-testid="pn-fat-empty-body" style={{ flex: 1, background: "white" }} />
        ) : (
        <>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 320px", gap: 12, padding: 12, flexShrink: 0 }}>
          <div style={{ border: "1px solid #ccc", padding: 10 }}>
            <Field label="NUMERO" value={doc.numero_fattura || ""} onChange={v => set("numero_fattura", v)} testid="pn-fat-numero" />
            <div style={{ display: "flex", gap: 6, alignItems: "stretch", marginBottom: 8 }}>
              <FieldDate label="DATA DOC." value={doc.data_fattura || ""} onChange={v => set("data_fattura", v)} testid="pn-fat-data" compact labelWidth={80} />
              <FieldDate label="REG." value={doc.data_registrazione || ""} testid="pn-fat-data-reg" compact readOnly labelWidth={40} />
            </div>
            {isExtra && (
              <Field
                label="DESCRIZIONE"
                value={doc.descrizione_extra || ""}
                onChange={v => set("descrizione_extra", v)}
                testid="pn-fat-descrizione-extra"
              />
            )}
            <FornitoreField doc={doc} setDoc={setDoc} />
            <PagamentoField doc={doc} setDoc={setDoc} />
            <button onClick={salva} disabled={saving} data-testid="pn-fat-save"
              style={{ marginTop: 12, padding: "10px 14px", background: "#1b5e20", color: "white", border: "none", fontSize: 13, fontWeight: 700, cursor: "pointer", width: "100%" }}>
              {saving ? "Salvataggio..." : "💾 Salva testata"}
            </button>
          </div>
          <div style={{ border: "1px solid #ccc", padding: 14, background: "#fafafa", display: "flex", flexDirection: "column", gap: 10 }}>
            <BigField label="TOTALE DOCUMENTO" value={doc.totale_iva_compresa || 0} editable onChange={v => set("totale_iva_compresa", Number(v))} testid="pn-fat-totale" color="#0d47a1" />
            <BigField label="TOTALE RIGHE" value={doc.totale_righe || 0} color="#37474f" testid="pn-fat-totale-righe" />
            {(() => {
              const liveDelta = Number(doc.totale_iva_compresa || 0) - Number(doc.totale_righe || 0);
              return <BigField label="DIFFERENZA" value={liveDelta} colorDelta={liveDelta} testid="pn-fat-delta" />;
            })()}
          </div>
        </div>
        {(Number(doc.totale_iva_compresa || 0) > 0 || (doc.righe || []).length > 0) ? (
        <>
        <div style={{ display: "flex", gap: 0, padding: "0 12px", flexShrink: 0, alignItems: "stretch" }}>
          <div style={{ padding: "6px 16px", background: "#1565c0", color: "white", fontSize: 12, fontWeight: 700, borderTop: "2px solid #0d47a1" }}>📋 Contabilità</div>
          <div style={{ padding: "6px 16px", background: "#cfd8dc", color: "#888", fontSize: 12, fontWeight: 700 }} title="Funzionalità futura">📅 Scadenzario</div>
          <div style={{ flex: 1 }} />
          <button onClick={async () => { await salva(); setShowAddRiga(true); }} data-testid="pn-fat-add-riga"
            style={{ padding: "4px 12px", background: "white", border: "1px solid #2e7d32", color: "#2e7d32", fontSize: 11, fontWeight: 700, cursor: "pointer", marginRight: 6 }}>+ Riga</button>
          <button onClick={async () => { await salva(); setShowRiparto(true); }} data-testid="pn-fat-riparto"
            style={{ padding: "4px 14px", background: "#0288d1", color: "white", border: "none", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>⚡ RIPARTO</button>
        </div>
        <div style={{ flex: 1, overflow: "auto", margin: "0 12px 12px", border: "1px solid #ccc" }}>
          <RigheTable righe={doc.righe || []} onDeleteAll={eliminaRiga} totale={doc.totale_iva_compresa} idDoc={id_doc} onChanged={ricarica} />
        </div>
        </>
        ) : (
        <div data-testid="pn-fat-righe-disabled" style={{ flex: 1, padding: "30px 18px", margin: "0 12px 12px", border: "2px dashed #ffd54f", background: "#fff8e1", color: "#5d4037", fontSize: 13, lineHeight: 1.5, textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
          <div style={{ fontSize: 30, marginBottom: 10 }}>📝</div>
          <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4, color: "#bf360c" }}>
            Imputa il <u>TOTALE DOCUMENTO</u> per poter aggiungere righe contabili
          </div>
          <div style={{ fontSize: 11, color: "#666", maxWidth: 480 }}>
            La sezione righe e i tasti <b>+ Riga</b> / <b>⚡ RIPARTO</b> si attivano automaticamente
            non appena il totale viene valorizzato nel campo a destra.
          </div>
        </div>
        )}
        </>
        )}
        {showRiparto && <RipartoWizard id_doc={id_doc} totale={doc.totale_iva_compresa} onClose={() => { setShowRiparto(false); ricarica(); }} />}
        {showAddRiga && <AddRigaModal id_doc={id_doc} onClose={() => { setShowAddRiga(false); ricarica(); }} />}
      </div>
    </div>
  );
}
